export const globalRoles = ["master_admin", "master_ops", "master_readonly"] as const;
export type GlobalRole = (typeof globalRoles)[number];

export const entityRoles = [
  "entity_admin",
  "resort_ops",
  "regional_manager",
  "oan",
  "payroll",
  "entity_readonly",
] as const;
export type EntityRole = (typeof entityRoles)[number];

export const scopeTypes = ["entity", "resort", "outlet"] as const;
export type ScopeType = (typeof scopeTypes)[number];

export const workspaceModes = ["master", "entity"] as const;
export type WorkspaceMode = (typeof workspaceModes)[number];

export const stepStatuses = [
  "not_started",
  "in_progress",
  "submitted",
  "complete",
  "rejected",
  "hold",
] as const;
export type StepStatus = (typeof stepStatuses)[number];

export const reviewStatuses = ["pending", "approved", "rejected", "resolved"] as const;
export type ReviewStatus = (typeof reviewStatuses)[number];

export const documentTypes = [
  "stock_state",
  "blind_count",
  "count_sheet_first",
  "count_sheet_second",
  "vento_capture_evidence",
  "stocktake_printout",
  "variance_report",
  "declaration",
  "deduction_letter",
  "wage_log",
  "payroll_confirmation",
] as const;
export type DocumentType = (typeof documentTypes)[number];

export const packDocumentOrder: readonly DocumentType[] = [
  "stock_state",
  "blind_count",
  "count_sheet_first",
  "count_sheet_second",
  "vento_capture_evidence",
  "stocktake_printout",
  "variance_report",
  "declaration",
  "deduction_letter",
  "wage_log",
  "payroll_confirmation",
];

export interface EntityRecord {
  id: string;
  slug: string;
  name: string;
  shortCode: string;
  status: "setup" | "active" | "archived";
  createdAt?: string;
}

export interface EntitySettingsRecord {
  entityId: string;
  supportEmail?: string | null;
  storageQuotaBytes: number;
  oneDriveRootFolder?: string | null;
  oneDriveSchedule?: string | null;
  notes?: string | null;
  updatedAt?: string | null;
}

export interface RequiredDocumentTemplateRecord {
  id: string;
  entityId: string;
  documentType: DocumentType;
  label: string;
  required: boolean;
  sortOrder: number;
  notes?: string | null;
}

export interface CounterProfileRecord {
  id: string;
  entityId: string;
  name: string;
  roleLabel: string;
  sortOrder: number;
  distinctGroup: string;
  notes?: string | null;
}

export interface EntityTemplateOverrideRecord {
  id: string;
  entityId: string;
  overrideKey: string;
  valueJson?: string | null;
  updatedAt?: string | null;
}

export interface CycleRecord {
  id: string;
  entityId: string;
  code: string;
  label: string;
  year: number;
  month: number;
  status: "bootstrap" | "active" | "archived";
  readOnly: boolean;
  sourceWorkbook?: string | null;
}

export interface ResortRecord {
  id: string;
  entityId: string;
  code: string;
  name: string;
}

export interface OutletRecord {
  id: string;
  entityId: string;
  resortId: string;
  code: string;
  name: string;
  active: boolean;
}

export interface WorkflowTemplate {
  stepNo: number;
  phase:
    | "Pre-Stocktake"
    | "Execution"
    | "Reconciliation"
    | "Accountability"
    | "Payroll"
    | "Finalisation";
  entity: "Resort" | "OAN" | "Senior Regional Manager" | "HR/Payroll";
  action: string;
  requiredDocument: string;
  requiredDocumentType?: DocumentType;
  predecessorStepNo: number | null;
  dueDay: number;
  ownerTitle: string;
  gateCode:
    | "stock_state_saved"
    | "blind_listing_saved"
    | "system_ready"
    | "first_count"
    | "second_count"
    | "third_party_vento"
    | "oan_reconciliation"
    | "manager_review"
    | "manager_approval"
    | "declaration_issued"
    | "deductions_issued"
    | "payroll_pack_sent"
    | "oan_payroll_verification"
    | "payroll_processed"
    | "final_pack_compiled";
}

export interface StocktakeRecord {
  id: string;
  entityId: string;
  cycleId: string;
  resortId: string;
  outletId: string;
  status: "not_started" | "in_progress" | "awaiting_review" | "ready_for_payroll" | "completed";
  readOnly: boolean;
  importedFrom?: string | null;
  notes?: string | null;
}

export interface StocktakeStepRecord {
  id: string;
  stocktakeId: string;
  stepNo: number;
  status: StepStatus;
  completedDate?: string | null;
  ownerName?: string | null;
  signOffName?: string | null;
  signOffDate?: string | null;
  notes?: string | null;
}

export interface UploadRecord {
  id: string;
  entityId: string;
  stocktakeId: string;
  cycleId: string;
  resortId: string;
  outletId: string;
  documentType: DocumentType;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  storageKey: string;
  checksum?: string | null;
  status: "uploaded" | "validated" | "archived";
  uploadedBy: string;
  uploadedAt: string;
  aiReviewState: "not_requested" | "queued" | "reviewed" | "fallback";
  extractedText?: string | null;
  metadataText?: string | null;
}

export interface QueryRecord {
  id: string;
  entityId: string;
  stocktakeId: string;
  stepNo: number;
  reason: string;
  status: "open" | "responded" | "approved" | "rejected";
  createdBy: string;
  assignedRole: EntityRole;
  response?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewRecord {
  id: string;
  entityId: string;
  stocktakeId: string;
  reviewType: "manager" | "oan" | "ai";
  status: ReviewStatus;
  reviewerId?: string | null;
  summary?: string | null;
  submittedAt: string;
  resolvedAt?: string | null;
}

export interface DeductionCaseRecord {
  id: string;
  entityId: string;
  stocktakeId?: string | null;
  resortId: string;
  outletId?: string | null;
  employeeNumber: string;
  employeeName: string;
  amount: number;
  salaryMonth: string;
  deductionDate: string;
  checkedByOan: boolean;
  checkedByHr: boolean;
  checkedByManager: boolean;
  managerComment?: string | null;
}

export interface PayrollSubmissionRecord {
  id: string;
  entityId: string;
  stocktakeId: string;
  status: "pending" | "verified" | "processed";
  submittedAt?: string | null;
  verifiedAt?: string | null;
  processedAt?: string | null;
  verificationNotes?: string | null;
}

export interface AuditEventRecord {
  id: string;
  entityId?: string | null;
  entityType: string;
  entityIdRef: string;
  action: string;
  actorUserId?: string | null;
  details?: string | null;
  createdAt: string;
}

export interface UserRecord {
  id: string;
  email: string;
  name: string;
  globalRole?: GlobalRole | null;
  active: boolean;
}

export interface EntityMembershipRecord {
  id: string;
  userId: string;
  entityId: string;
  isDefault: boolean;
}

export interface EntityRoleAssignmentRecord {
  id: string;
  membershipId: string;
  entityId: string;
  role: EntityRole;
  scopeType: ScopeType;
  scopeId?: string | null;
}

export interface EntityMembership extends EntityMembershipRecord {
  entity: EntityRecord;
  assignments: EntityRoleAssignmentRecord[];
  user?: UserRecord;
}

export interface WorkspaceContext {
  mode: WorkspaceMode;
  entityId?: string | null;
  entityName?: string | null;
  impersonatingEntityId?: string | null;
  impersonatingEntityName?: string | null;
}

export interface AuthUser extends UserRecord {
  memberships: EntityMembership[];
  activeWorkspace: WorkspaceContext;
}

export interface StocktakeComputedState {
  predecessorComplete: boolean;
  canProceed: boolean;
  daysLate: number;
  exceptionFlag: string;
  payrollReady: boolean;
  finalPackComplete: boolean;
  missingDocuments: DocumentType[];
}

export interface LegacyChecklistSnapshot {
  ventoCompleted: boolean | null;
  lossesAndSurpluses: string;
  declarationPresent: boolean;
  declarationVarianceText: string;
  deductionPresent: boolean;
  deductionValue?: number | null;
  blindCountPresent: boolean;
  printoutsPresent: boolean;
  stockStatePresent: boolean;
  notes?: string | null;
}

export interface StocktakeSummary {
  entity: EntityRecord;
  stocktake: StocktakeRecord;
  cycle: CycleRecord;
  resort: ResortRecord;
  outlet: OutletRecord;
  steps: StocktakeStepRecord[];
  uploads: UploadRecord[];
  queries: QueryRecord[];
  reviews: ReviewRecord[];
  deductions: DeductionCaseRecord[];
  payrollSubmission?: PayrollSubmissionRecord | null;
  computed: StocktakeComputedState;
  requiredDocuments: RequiredDocumentTemplateRecord[];
  counterProfiles: CounterProfileRecord[];
  legacySnapshot?: LegacyChecklistSnapshot | null;
}

export interface DashboardMetric {
  label: string;
  value: string | number;
  tone?: "default" | "good" | "warn" | "danger";
}

export interface DashboardResponse {
  currentCycle: CycleRecord | null;
  entity: EntityRecord | null;
  metrics: DashboardMetric[];
  myQueue: StocktakeSummary[];
  exceptions: StocktakeSummary[];
  payrollQueue: StocktakeSummary[];
  cycleSummaries: Array<{
    cycle: CycleRecord;
    completed: number;
    total: number;
    completionRate: number;
    exceptionCount: number;
  }>;
}

export interface EntitySummary {
  entity: EntityRecord;
  settings: EntitySettingsRecord | null;
  resortCount: number;
  outletCount: number;
  memberCount: number;
  stocktakeCount: number;
  exceptionCount: number;
  storageBytes: number;
  latestBackupAt?: string | null;
}

export interface StorageUsageBreakdown {
  key: string;
  label: string;
  totalBytes: number;
  fileCount: number;
}

export interface StorageUsageEntity {
  entityId: string;
  entityName: string;
  totalBytes: number;
  fileCount: number;
  byCycle: StorageUsageBreakdown[];
  byDocumentType: StorageUsageBreakdown[];
  byOutlet: StorageUsageBreakdown[];
}

export interface StorageUsageReport {
  totalBytes: number;
  totalFiles: number;
  updatedAt: string;
  entities: StorageUsageEntity[];
}

export interface BackupManifestFile {
  storageKey: string;
  fileName: string;
  documentType?: DocumentType;
  sizeBytes: number;
  uploadedAt?: string;
}

export interface BackupManifest {
  entityId: string;
  entityName: string;
  generatedAt: string;
  fileCount: number;
  totalBytes: number;
  exports: string[];
  files: BackupManifestFile[];
}

export interface BackupTargetConfig {
  id: string;
  provider: "onedrive";
  configured: boolean;
  enabled: boolean;
  tenantId?: string | null;
  clientId?: string | null;
  clientSecretConfigured: boolean;
  driveId?: string | null;
  rootFolder?: string | null;
  schedule?: string | null;
  lastValidatedAt?: string | null;
}

export interface BackupJobSummary {
  id: string;
  entityId: string;
  entityName: string;
  status: "pending" | "running" | "completed" | "failed";
  triggerMode: "manual" | "scheduled";
  totalBytes: number;
  fileCount: number;
  startedAt: string;
  completedAt?: string | null;
  errorText?: string | null;
  manifest?: BackupManifest | null;
}

export interface EntityWorkspaceBundle {
  entity: EntityRecord;
  settings: EntitySettingsRecord | null;
  resorts: ResortRecord[];
  outlets: OutletRecord[];
  requiredDocuments: RequiredDocumentTemplateRecord[];
  counterProfiles: CounterProfileRecord[];
  members: EntityMembership[];
  templateOverrides: EntityTemplateOverrideRecord[];
}

export interface MasterOverviewResponse {
  metrics: DashboardMetric[];
  entities: EntitySummary[];
  storage: StorageUsageReport;
  backupTarget: BackupTargetConfig | null;
  backupJobs: BackupJobSummary[];
}

export interface ApiResponse<T> {
  data: T;
}
